/**
 * HamburgerMenu.js
 * Minimal, elegant hamburger menu with navigation and social links
 * Routes all navigation through NavigationController
 */

export class HamburgerMenu {
    constructor(navigationController) {
        this.navigationController = navigationController;
        this.isOpen = false;
        this.contactFormActive = false;
        
        // Create DOM elements
        this.createElements();
        this.attachEventListeners();
    }

    createElements() {
        // Create hamburger button (top-left, mirrors close button style)
        this.button = document.createElement('button');
        this.button.className = 'circular-btn circular-btn-hamburger';
        this.button.setAttribute('aria-label', 'Menu');
        
        // Hamburger icon (three lines)
        this.button.innerHTML = `
            <svg width="24" height="24" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                <line class="hamburger-line hamburger-line-top" x1="3" y1="6" x2="17" y2="6" stroke="white" stroke-width="2" stroke-linecap="round"/>
                <line class="hamburger-line hamburger-line-middle" x1="3" y1="10" x2="17" y2="10" stroke="white" stroke-width="2" stroke-linecap="round"/>
                <line class="hamburger-line hamburger-line-bottom" x1="3" y1="14" x2="17" y2="14" stroke="white" stroke-width="2" stroke-linecap="round"/>
            </svg>
        `;
        
        // Create menu drawer
        this.drawer = document.createElement('div');
        this.drawer.className = 'hamburger-drawer';
        
        // Logo
        const logoContainer = document.createElement('div');
        logoContainer.className = 'hamburger-logo-container';
        logoContainer.innerHTML = `
            <img src="/images/br-logo.svg" alt="BR Logo" class="hamburger-logo" />
        `;
        
        // Navigation items container
        this.navContainer = document.createElement('nav');
        this.navContainer.className = 'hamburger-nav';
        this.navContainer.innerHTML = `
            <a href="#" class="hamburger-nav-item" data-action="home">Home</a>
            <a href="#" class="hamburger-nav-item" data-action="about">Story</a>
            <a href="#" class="hamburger-nav-item" data-action="info">Info</a>
            <a href="#" class="hamburger-nav-item" data-action="contact">Contact</a>
        `;
        
        // Contact form container (initially hidden)
        this.contactForm = document.createElement('form');
        this.contactForm.className = 'hamburger-contact-form';
        this.contactForm.style.display = 'none';
        this.contactForm.innerHTML = `
            <input type="text" name="name" placeholder="Name" class="hamburger-form-input" required />
            <input type="email" name="email" placeholder="Email" class="hamburger-form-input" required />
            <textarea name="message" placeholder="Message" class="hamburger-form-textarea" required></textarea>
            <div class="hamburger-form-buttons">
                <button type="button" class="hamburger-form-button" data-action="cancel">Cancel</button>
                <button type="submit" class="hamburger-form-button hamburger-form-button-send">Send</button>
            </div>
            <div class="hamburger-form-message" style="display: none;"></div>
        `;
        
        // Vimeo video overlay (initially hidden)
        this.vimeoOverlay = document.createElement('div');
        this.vimeoOverlay.className = 'vimeo-overlay';
        this.vimeoOverlay.style.display = 'none';
        this.vimeoOverlay.innerHTML = `
            <button class="circular-btn circular-btn-close vimeo-close-btn" aria-label="Close video">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M19 6.41L17.59 5L12 10.59L6.41 5L5 6.41L10.59 12L5 17.59L6.41 19L12 13.41L17.59 19L19 17.59L13.41 12L19 6.41Z" fill="white"/>
                </svg>
            </button>
            <div class="vimeo-container">
                <div class="vimeo-wrapper">
                    <div class="vimeo-click-blocker vimeo-click-blocker-left"></div>
                    <div class="vimeo-click-blocker vimeo-click-blocker-right"></div>
                    <iframe src="https://player.vimeo.com/video/1023033340?h=a348cf08f8&texttrack=en&badge=0&autopause=0&player_id=0&app_id=58479" 
                            frameborder="0" 
                            allow="autoplay; fullscreen; picture-in-picture; clipboard-write; encrypted-media; web-share" 
                            referrerpolicy="strict-origin-when-cross-origin" 
                            style="position:absolute;top:0;left:0;width:100%;height:100%;" 
                            title="Burton Rast - 'Lost and Found'">
                    </iframe>
                </div>
                <p class="vimeo-caption"><em>Lost and Found</em>, the story of my images.</p>
            </div>
        `;
        
        // Social icons container
        const socialContainer = document.createElement('div');
        socialContainer.className = 'hamburger-social';
        socialContainer.innerHTML = `
            <a href="https://www.instagram.com/misterburton/" target="_blank" rel="noopener noreferrer" class="hamburger-social-link" aria-label="Instagram">
                <svg width="32" height="32" viewBox="0 0 600 600" xmlns="http://www.w3.org/2000/svg">
                    <circle cx="300" cy="300" r="297.5" fill="#fff"/>
                    <path d="M300,123.5c-47.9,0-54,.2-72.8,1.1s-31.6,3.8-42.8,8.2c-11.6,4.5-21.5,10.5-31.3,20.4-9.8,9.8-15.8,19.7-20.4,31.3-4.4,11.2-7.4,24.1-8.2,42.8-.8,18.8-1.1,24.8-1.1,72.8s.2,53.9,1.1,72.8c.9,18.8,3.8,31.6,8.2,42.8,4.5,11.6,10.5,21.5,20.4,31.3,9.8,9.8,19.7,15.9,31.3,20.4,11.2,4.4,24.1,7.3,42.8,8.2,18.8.9,24.8,1.1,72.8,1.1s53.9-.2,72.8-1.1c18.8-.9,31.6-3.8,42.9-8.2,11.6-4.5,21.4-10.6,31.2-20.4,9.8-9.8,15.8-19.7,20.4-31.3,4.3-11.2,7.3-24,8.2-42.8.8-18.8,1.1-24.8,1.1-72.8s-.2-53.9-1.1-72.8c-.9-18.8-3.9-31.6-8.2-42.8-4.5-11.6-10.6-21.5-20.4-31.3s-19.6-15.8-31.3-20.4c-11.3-4.4-24.1-7.3-42.9-8.2-18.8-.9-24.8-1.1-72.7-1.1h0ZM284.2,155.3h15.8c47.1,0,52.7.2,71.3,1,17.2.8,26.5,3.7,32.8,6.1,8.2,3.2,14.1,7,20.3,13.2,6.2,6.2,10,12.1,13.2,20.3,2.4,6.2,5.3,15.6,6.1,32.8.8,18.6,1,24.2,1,71.3s-.2,52.7-1,71.3c-.8,17.2-3.7,26.5-6.1,32.8-3.2,8.2-7,14.1-13.2,20.3-6.2,6.2-12,10-20.3,13.2-6.2,2.4-15.6,5.3-32.8,6.1-18.6.8-24.2,1-71.3,1s-52.7-.2-71.3-1c-17.2-.8-26.5-3.7-32.8-6.1-8.2-3.2-14.1-7-20.3-13.2s-10-12.1-13.2-20.3c-2.4-6.2-5.3-15.6-6.1-32.8-.8-18.6-1-24.2-1-71.3s.2-52.7,1-71.3c.8-17.2,3.7-26.5,6.1-32.8,3.2-8.2,7-14.1,13.2-20.3s12.1-10,20.3-13.2c6.2-2.4,15.6-5.3,32.8-6.1,16.3-.8,22.6-1,55.5-1h0ZM394.2,184.6c-11.7,0-21.2,9.5-21.2,21.2s9.5,21.2,21.2,21.2,21.2-9.5,21.2-21.2c0-11.7-9.5-21.2-21.2-21.2h0ZM300,209.4c-50.1,0-90.6,40.6-90.6,90.6s40.6,90.6,90.6,90.6,90.6-40.6,90.6-90.6-40.5-90.6-90.6-90.6h0ZM300,241.2c32.5,0,58.8,26.3,58.8,58.8s-26.3,58.8-58.8,58.8-58.8-26.3-58.8-58.8,26.3-58.8,58.8-58.8Z"/>
                </svg>
            </a>
            <a href="https://glass.photo/misterburton" target="_blank" rel="noopener noreferrer" class="hamburger-social-link" aria-label="Glass">
                <svg width="32" height="32" viewBox="0 0 32 32" xmlns="http://www.w3.org/2000/svg">
                    <path fill="#fff" d="M0,16C0,7.16,7.16,0,16,0s16,7.16,16,16-7.16,16-16,16S0,24.84,0,16Z"/>
                    <path d="M18.51,22.19c0,1.16-.16,2.02-.47,2.56-.29.5-.59.69-1.09.69-.34,0-.62-.18-.76-.49-.24-.55-.02-1.29.59-1.99.37-.42,1-.99,1.72-1.61v.84ZM21.82,16.68c-.43.35-.91.74-1.42,1.15-.14.12-.29.23-.43.35v-3.81c0-.36-.23-.67-.56-.75-.33-.08-.67.08-.82.4,0,.02-.79,1.63-1.98,3.03-1.13,1.34-2.18,1.99-3.22,1.99-.79,0-1.55-.41-2.08-1.12-.57-.75-.85-1.8-.85-3.11,0-2.05.52-3.85,1.59-5.52,1.13-1.75,2.69-2.76,4.27-2.76,1.45,0,2.34.89,2.34,2.32,0,1.27-.72,2.36-2.32,3.53-.33.24-.42.73-.19,1.08.23.35.68.44,1.01.2,2.02-1.48,2.95-3.01,2.95-4.81,0-2.28-1.56-3.87-3.8-3.87-2.07,0-4.07,1.25-5.48,3.43-1.24,1.93-1.85,4.02-1.85,6.39,0,1.67.39,3.04,1.17,4.07.81,1.08,1.99,1.7,3.22,1.7,1.47,0,2.88-.82,4.3-2.5.3-.35.57-.71.81-1.06v2.34c-1.15.95-2.2,1.86-2.79,2.53-1.04,1.18-1.36,2.57-.85,3.71.38.85,1.17,1.38,2.08,1.38,1.02,0,1.78-.47,2.34-1.43.46-.8.68-1.9.68-3.37v-2.06c.45-.36.9-.73,1.32-1.07.51-.41,1-.81,1.43-1.16.32-.26.38-.75.13-1.09-.25-.34-.71-.4-1.02-.14Z"/>
                </svg>
            </a>
        `;
        
        // Assemble drawer
        this.drawer.appendChild(logoContainer);
        this.drawer.appendChild(this.navContainer);
        this.drawer.appendChild(this.contactForm);
        this.drawer.appendChild(socialContainer);
        
        // Add to DOM
        document.body.appendChild(this.button);
        document.body.appendChild(this.drawer);
        document.body.appendChild(this.vimeoOverlay);
    }

    attachEventListeners() {
        // Toggle menu
        this.button.addEventListener('click', (e) => {
            e.stopPropagation();
            this.toggle();
        });
        
        // Logo click - navigate to overview (only if contact form is not active)
        const logo = this.drawer.querySelector('.hamburger-logo-container');
        logo.addEventListener('click', (e) => {
            if (!this.contactFormActive) {
                e.preventDefault();
                this.navigateToOverview();
            } else {
                e.preventDefault();
            }
        });
        
        // Navigation items - handle Home and Contact
        const navItems = this.navContainer.querySelectorAll('.hamburger-nav-item[data-action]');
        navItems.forEach(item => {
            item.addEventListener('click', (e) => {
                e.preventDefault();
                const action = item.getAttribute('data-action');
                this.handleNavigation(action);
            });
        });
        
        // Contact form handlers
        const cancelButton = this.contactForm.querySelector('[data-action="cancel"]');
        cancelButton.addEventListener('click', () => {
            this.hideContactForm();
        });
        
        this.contactForm.addEventListener('submit', (e) => {
            e.preventDefault();
            this.handleContactSubmit();
        });
        
        // Vimeo close button
        const vimeoCloseBtn = this.vimeoOverlay.querySelector('.vimeo-close-btn');
        vimeoCloseBtn.addEventListener('click', () => {
            this.hideVimeoOverlay();
        });
        
        // Vimeo overlay - click outside to close
        this.vimeoOverlay.addEventListener('click', (e) => {
            // Close if clicking on click-blocker divs, overlay background, or anywhere outside video/caption
            const isClickBlocker = e.target.classList.contains('vimeo-click-blocker');
            const isOverlayBackground = e.target === this.vimeoOverlay;
            
            if (isClickBlocker || isOverlayBackground) {
                this.hideVimeoOverlay();
            }
        });
        
        // Outside click to close drawer (desktop only)
        this.drawer.addEventListener('click', (e) => {
            // Only close if clicking the drawer background itself, not its contents
            if (e.target === this.drawer) {
                this.close();
            }
        });
        
        // Prevent clicks inside menu from closing it
        const menuContent = this.drawer.querySelectorAll('.hamburger-logo-container, .hamburger-nav, .hamburger-social');
        menuContent.forEach(el => {
            el.addEventListener('click', (e) => {
                e.stopPropagation();
            });
        });
    }

    handleNavigation(action) {
        switch (action) {
            case 'home':
                this.navigateToOverview();
                break;
            case 'about':
                this.showVimeoOverlay();
                break;
            case 'info':
                this.navigationController?.infoModal?.open();
                break;
            case 'contact':
                this.showContactForm();
                break;
        }
    }
    
    showVimeoOverlay() {
        // Fade out drawer and menu content together
        const menuContent = [
            this.drawer.querySelector('.hamburger-logo-container'),
            this.navContainer,
            this.contactForm,
            this.drawer.querySelector('.hamburger-social')
        ];

        // Kill any in-flight tweens on elements we're about to animate
        gsap.killTweensOf([this.drawer, this.button, this.vimeoOverlay, ...menuContent]);

        gsap.to([this.drawer, this.button, ...menuContent], {
            opacity: 0,
            duration: 0.2,
            ease: 'power2.in',
            onComplete: () => {
                // Hide drawer and button
                this.drawer.classList.remove('open');
                this.button.classList.remove('open');
                this.isOpen = false;
                
                menuContent.forEach(el => {
                    if (el) el.style.display = 'none';
                });
                
                // Show video
                gsap.set(this.vimeoOverlay, { display: 'flex', opacity: 0 });
                gsap.to(this.vimeoOverlay, {
                    opacity: 1,
                    duration: 0.3,
                    ease: 'power2.out'
                });
            }
        });
    }
    
    hideVimeoOverlay() {
        gsap.killTweensOf(this.vimeoOverlay);
        gsap.to(this.vimeoOverlay, {
            opacity: 0,
            duration: 0.3,
            ease: 'power2.in',
            onComplete: () => {
                this.vimeoOverlay.style.display = 'none';
                
                // Pause the video when closed
                const iframe = this.vimeoOverlay.querySelector('iframe');
                if (iframe && iframe.contentWindow) {
                    iframe.contentWindow.postMessage('{"method":"pause"}', '*');
                }
                
                // Reset state without reopening menu
                this.isOpen = false;
                document.body.style.overflow = '';
                
                // Reset drawer and button visibility for next use
                this.drawer.style.opacity = '';
                this.button.style.display = '';
                this.button.style.opacity = '';
                
                // Reset only the visible menu elements (NOT contact form)
                const logoContainer = this.drawer.querySelector('.hamburger-logo-container');
                const socialContainer = this.drawer.querySelector('.hamburger-social');
                
                if (logoContainer) {
                    logoContainer.style.display = '';
                    logoContainer.style.opacity = '';
                }
                if (this.navContainer) {
                    this.navContainer.style.display = '';
                    this.navContainer.style.opacity = '';
                }
                if (socialContainer) {
                    socialContainer.style.display = '';
                    socialContainer.style.opacity = '';
                }
                
                // Ensure contact form stays hidden
                this.contactForm.style.display = 'none';
                this.contactForm.style.opacity = '';

                // Restore info button if in OVERVIEW state
                const infoModal = this.navigationController?.infoModal;
                if (infoModal && !infoModal.isOpen && this.navigationController?.state === 'OVERVIEW') {
                    gsap.killTweensOf(infoModal.button);
                    gsap.to(infoModal.button, { autoAlpha: 1, duration: 0.2, ease: 'power2.out' });
                }
            }
        });
    }
    
    showContactForm() {
        this.contactFormActive = true;

        // Get form elements
        const formInputs = this.contactForm.querySelectorAll('.hamburger-form-input, .hamburger-form-textarea');
        const formButtons = this.contactForm.querySelector('.hamburger-form-buttons');

        // Kill any in-flight tweens on elements we're about to animate
        gsap.killTweensOf([this.navContainer, this.contactForm, ...formInputs, formButtons]);

        // Set initial states
        gsap.set(this.contactForm, { display: 'flex', opacity: 0 });
        gsap.set(formInputs, { opacity: 0, y: -10 });
        gsap.set(formButtons, { opacity: 0, y: -10 });
        
        // Animate out nav items
        const tl = gsap.timeline();
        tl.to(this.navContainer.querySelectorAll('.hamburger-nav-item'), {
            opacity: 0,
            y: -10,
            duration: 0.1,
            stagger: 0.025,
            ease: 'power2.in'
        })
        .set(this.navContainer, { display: 'none' })
        .to(this.contactForm, {
            opacity: 1,
            duration: 0.1,
            ease: 'power2.out'
        })
        .to(formInputs, {
            opacity: 1,
            y: 0,
            duration: 0.15,
            stagger: 0.04,
            ease: 'power2.out'
        }, '-=0.05')
        .to(formButtons, {
            opacity: 1,
            y: 0,
            duration: 0.15,
            ease: 'power2.out'
        }, '-=0.1')
        .call(() => {
            // Focus first input after animation
            const firstInput = this.contactForm.querySelector('input[name="name"]');
            if (firstInput) {
                firstInput.focus();
            }
        });
    }
    
    hideContactForm() {
        this.contactFormActive = false;

        // Get form elements
        const formInputs = this.contactForm.querySelectorAll('.hamburger-form-input, .hamburger-form-textarea');
        const formButtons = this.contactForm.querySelector('.hamburger-form-buttons');
        const messageEl = this.contactForm.querySelector('.hamburger-form-message');

        // Kill any in-flight tweens on elements we're about to animate
        gsap.killTweensOf([this.navContainer, this.contactForm, ...formInputs, formButtons]);

        // Animate out form first
        const tl = gsap.timeline();
        tl.to(formButtons, {
            opacity: 0,
            y: -10,
            duration: 0.1,
            ease: 'power2.in'
        })
        .to(formInputs, {
            opacity: 0,
            y: -10,
            duration: 0.1,
            stagger: 0.025,
            ease: 'power2.in'
        }, '-=0.05')
        .to(this.contactForm, {
            opacity: 0,
            duration: 0.1,
            ease: 'power2.in'
        }, '-=0.05')
        .set(this.contactForm, { display: 'none' })
        .set(this.navContainer, { display: 'flex' })
        .set(this.navContainer.querySelectorAll('.hamburger-nav-item'), { opacity: 0, y: 10 })
        .to(this.navContainer.querySelectorAll('.hamburger-nav-item'), {
            opacity: 1,
            y: 0,
            duration: 0.15,
            stagger: 0.04,
            ease: 'power2.out'
        })
        .call(() => {
            // Clear form after animation
            this.contactForm.reset();
            
            // Hide any messages
            if (messageEl) {
                messageEl.style.display = 'none';
            }
        });
    }
    
    async handleContactSubmit() {
        const formData = new FormData(this.contactForm);
        const name = formData.get('name');
        const email = formData.get('email');
        const message = formData.get('message');

        // Basic validation
        if (!name || !email || !message) {
            this.showFormMessage('Please fill out all fields.', 'error');
            return;
        }

        const submitButton = this.contactForm.querySelector('button[type="submit"]');
        submitButton.disabled = true;
        submitButton.textContent = 'Sending…';

        try {
            const { getTurnstileToken } = await import('../utils/TurnstileWidget.js');
            const turnstileToken = await getTurnstileToken();

            const res = await fetch('/api/send-inquiry', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    name, email, message, turnstileToken,
                    subject: 'Inquiry from burtonrast.com',
                }),
            });

            const data = await res.json();

            if (!res.ok) {
                throw new Error(data.error || 'Failed to send message.');
            }

            this.showFormResult('success');
        } catch (err) {
            this.showFormResult('error', err.message || 'Something went wrong. Please try again.');
        } finally {
            submitButton.disabled = false;
            submitButton.textContent = 'Send';
        }
    }

    showFormResult(type, errorMessage) {
        const isSuccess = type === 'success';
        const color = isSuccess ? '#6fdb6f' : '#ff6b6b';
        const label = isSuccess ? 'Message sent' : (errorMessage || 'Failed to send');

        // SVG paths with stroke-dasharray for draw animation
        // Checkmark: polyline length ≈ 29
        // X: each line length ≈ 22.6
        const iconPath = isSuccess
            ? '<polyline points="20,31 27,38 40,23" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round" stroke-linejoin="round" stroke-dasharray="29" stroke-dashoffset="29" style="visibility: hidden;" />'
            : '<line x1="22" y1="22" x2="38" y2="38" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round" stroke-dasharray="23" stroke-dashoffset="23" /><line x1="38" y1="22" x2="22" y2="38" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round" stroke-dasharray="23" stroke-dashoffset="23" />';

        // Hide form fields, show result
        const fields = this.contactForm.querySelectorAll('.hamburger-form-input, .hamburger-form-textarea, .hamburger-form-buttons, .hamburger-form-message');
        fields.forEach(el => el.style.display = 'none');

        // Create result element
        let resultEl = this.contactForm.querySelector('.hamburger-form-result');
        if (!resultEl) {
            resultEl = document.createElement('div');
            resultEl.className = 'hamburger-form-result';
            this.contactForm.appendChild(resultEl);
        }

        resultEl.style.display = 'flex';
        resultEl.style.cssText = `
            display: flex; flex-direction: column; align-items: center; justify-content: center;
            padding: 40px 20px; min-height: 200px;
        `;
        resultEl.innerHTML = `
            <svg width="60" height="60" viewBox="0 0 60 60" fill="none" style="color: ${color};">
                <circle cx="30" cy="30" r="26" stroke="currentColor" stroke-width="2"
                    stroke-dasharray="163.36" stroke-dashoffset="163.36" />
                ${iconPath}
            </svg>
            <p style="color: rgba(255,255,255,0.5); font-size: 14px; margin-top: 16px;
                font-family: 'IBM Plex Mono', monospace; opacity: 0;">${label}</p>
        `;

        // Animate with GSAP
        const circle = resultEl.querySelector('circle');
        const text = resultEl.querySelector('p');

        gsap.to(circle, { strokeDashoffset: 0, duration: 0.6, ease: 'power2.out' });

        if (isSuccess) {
            const check = resultEl.querySelector('polyline');
            gsap.set(check, { visibility: 'visible', delay: 0.5 });
            gsap.to(check, { strokeDashoffset: 0, duration: 0.4, delay: 0.5, ease: 'power2.out' });
        } else {
            const lines = resultEl.querySelectorAll('line');
            gsap.to(lines[0], { strokeDashoffset: 0, duration: 0.3, delay: 0.5, ease: 'power2.out' });
            gsap.to(lines[1], { strokeDashoffset: 0, duration: 0.3, delay: 0.65, ease: 'power2.out' });
        }

        gsap.to(text, { opacity: 1, duration: 0.3, delay: 0.8, ease: 'power2.out' });

        if (isSuccess) {
            this.contactForm.reset();
        }

        // After the animation plays, transition back to nav menu
        setTimeout(() => {
            this.restoreFormFields();
        }, isSuccess ? 3000 : 4000);
    }

    restoreFormFields() {
        const resultEl = this.contactForm.querySelector('.hamburger-form-result');
        if (resultEl) {
            gsap.to(resultEl, {
                opacity: 0, duration: 0.3, ease: 'power2.in',
                onComplete: () => {
                    resultEl.remove();

                    // Restore form field visibility while form is still hidden
                    const fields = this.contactForm.querySelectorAll('.hamburger-form-input, .hamburger-form-textarea, .hamburger-form-buttons');
                    fields.forEach(el => el.style.display = '');

                    // Hide the form immediately (no animate-out, it's already invisible via result fade)
                    // Then animate the nav items back in
                    this.contactFormActive = false;
                    this.contactForm.style.display = 'none';
                    this.contactForm.style.opacity = '';

                    const messageEl = this.contactForm.querySelector('.hamburger-form-message');
                    if (messageEl) messageEl.style.display = 'none';
                    this.contactForm.reset();

                    // Animate nav items in
                    gsap.set(this.navContainer, { display: 'flex' });
                    gsap.set(this.navContainer.querySelectorAll('.hamburger-nav-item'), { opacity: 0, y: 10 });
                    gsap.to(this.navContainer.querySelectorAll('.hamburger-nav-item'), {
                        opacity: 1,
                        y: 0,
                        duration: 0.15,
                        stagger: 0.04,
                        ease: 'power2.out'
                    });
                }
            });
        }
    }
    
    showFormMessage(message, type) {
        const messageEl = this.contactForm.querySelector('.hamburger-form-message');
        if (messageEl) {
            messageEl.textContent = message;
            messageEl.className = `hamburger-form-message hamburger-form-message-${type}`;
            messageEl.style.display = 'block';
        }
    }

    navigateToOverview() {
        // Route through NavigationController - same as "View All Images" in context menu
        if (this.navigationController) {
            // Use the same logic as context menu's "View All Images" action
            const currentState = this.navigationController.state;
            if (currentState === 'DETAIL_VIEW') {
                this.navigationController.navigate('back-to-contact');
            } else if (currentState === 'ZOOMED_IN') {
                this.navigationController.navigate('zoom-out');
            }
            // If already in OVERVIEW, do nothing
        }
        this.close();
    }

    toggle() {
        if (this.isOpen) {
            this.close();
        } else {
            this.open();
        }
    }

    open() {
        this.isOpen = true;
        this.drawer.classList.add('open');
        this.button.classList.add('open');
        document.body.style.overflow = 'hidden'; // Prevent body scroll

        // Close info modal if open (mutually exclusive), without restoring its button
        if (this.navigationController?.infoModal?.isOpen) {
            this.navigationController.infoModal.close({ restoreButton: false });
        }

        // Fade out info button while hamburger is open
        const infoButton = this.navigationController?.infoModal?.button;
        if (infoButton) {
            gsap.killTweensOf(infoButton);
            gsap.to(infoButton, { autoAlpha: 0, duration: 0.2, ease: 'power2.in' });
        }
    }

    close() {
        this.isOpen = false;
        this.drawer.classList.remove('open');
        this.button.classList.remove('open');
        document.body.style.overflow = ''; // Restore body scroll

        // If contact form is active, hide it
        if (this.contactFormActive) {
            this.hideContactForm();
        }

        // Restore info button if we're in OVERVIEW and the info modal isn't open
        const infoModal = this.navigationController?.infoModal;
        if (infoModal && !infoModal.isOpen && this.navigationController?.state === 'OVERVIEW') {
            gsap.killTweensOf(infoModal.button);
            gsap.to(infoModal.button, { autoAlpha: 1, duration: 0.2, ease: 'power2.out', delay: 0.1 });
        }

        // Restore hamburger menu content visibility
        const menuContent = [
            this.button,
            this.drawer.querySelector('.hamburger-logo-container'),
            this.navContainer,
            this.drawer.querySelector('.hamburger-social')
        ];
        
        menuContent.forEach(el => {
            if (el) {
                el.style.display = '';
                el.style.opacity = '';
            }
        });
    }

    dispose() {
        // Clean up
        if (this.button && this.button.parentNode) {
            this.button.parentNode.removeChild(this.button);
        }
        if (this.drawer && this.drawer.parentNode) {
            this.drawer.parentNode.removeChild(this.drawer);
        }
    }
}

